import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { CiUser } from "react-icons/ci";
import { PiExamDuotone } from "react-icons/pi";
import "../../styles/globle.css";
import { useAuth } from "../../contaxt/AuthContext";
import { useIsMobile } from "../../hooks/use-mobile";
import logo from '../../../public/images/navLogo.svg'
import profile from '../../../public/images/userProfile.svg'
import logout from '../../../public/images/logout.svg'
import subscription from '../../../public/images/subscription.svg'
import help from '../../../public/images/helpCenter.svg'
import userProfile from '../../../public/images/userProfileNav.svg'
import plan from '../../../public/images/plan.svg'
import apiClient from "../../api/apiClient";
import ExitExamDialog from "../models/ExitExamDialog";

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, handleLogout } = useAuth();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userPlan, setUserPlan] = useState<string>("Expired"); 
  const [showExitModal, setShowExitModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Helper function to stop all audio and recording
  const stopAllAudio = () => {
    console.log("🛑 Stopping all audio from navbar navigation");

    // Stop recording and audio service
    if ((window as any).audioServiceRef?.current) {
      (window as any).audioServiceRef.current.stopRecording();
      (window as any).audioServiceRef.current.cancelAIAudio?.();
    }

    // Stop speech recognition
    if ((window as any).speechRecognitionRef?.current) {
      (window as any).speechRecognitionRef.current.cleanup();
    }

    // Stop any local audio elements
    try {
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
        audio.src = '';
      });

      // Stop Web Audio API contexts
      const audioContext = (window as any).__appAudioContext;
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().catch(() => { });
      }
    } catch (error) {
      console.warn('Error during audio cleanup from navbar:', error);
    }
  };

  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem("access_token");
      setIsLoggedIn(!!token);
      if (isLoggedIn) {
        navigate("/dashboard");
      }
    };

    checkToken();

    window.addEventListener("login", checkToken);
    window.addEventListener("logout", checkToken);

    return () => {
      window.removeEventListener("login", checkToken);
      window.removeEventListener("logout", checkToken);
    };
  }, []);

  // Fetch user's subscription plan
  useEffect(() => {
    const fetchUserPlan = async () => {
      if (!isAuthenticated) return;

      try {
        const response = await apiClient.post("/get_user_subscriptions", {});
        const subscriptions = response.data?.subscriptions || [];

        // Get the active or cancel_scheduled subscription
        const activeSub = subscriptions.find(
          (sub: any) => sub.status === "active" || sub.status === "cancel_scheduled"
        );

        if (activeSub?.plan_name) {
          setUserPlan(activeSub.plan_name);
        } else {
          setUserPlan("Expired");
        }
      } catch (error) {
        console.error("Error fetching subscription plan:", error);
        setUserPlan("Expired");
      }
    };

    fetchUserPlan();
  }, [isAuthenticated]);


  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle logo click - show exit confirmation on task pages; otherwise prep2pass.ca (marketing) or dashboard
  const handleLogoClick = () => {
    if (location.pathname.startsWith('/task')) {
      setShowExitModal(true);
    } else if (isAuthenticated) {
      navigate("/dashboard");
    } else {
      window.location.href = "https://prep2pass.ca";
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 w-full bg-white border-b border-gray-200 shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          {/* Logo */}
          <div className="cursor-pointer navbar-logo" onClick={handleLogoClick}>
            <img src={logo} alt="" />
            <span>Prep2Pass - TCF Speaking</span>
            {isAuthenticated && !isMobile && <a href="/dashboard" className="navbar-dashboard-link" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation(); // Don't trigger logo click (exit modal) when clicking Dashboard
              if (location.pathname.startsWith('/task')) {
                stopAllAudio();
              }
              navigate("/dashboard");
            }}>Dashboard</a>}
          </div>

          {/* Profile Dropdown */}
          <div className="right-container">
            {!isMobile && isAuthenticated && (
              <div className="plan-container">
                <img src={plan} alt="" />
                <span>{userPlan} Plan</span>
              </div>
            )}
            <div className="relative cursor-pointer" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300 focus:outline-none"
              >
                <img src={profile} alt="" />
                {/* You can replace "U" with user avatar image if available */}
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-[240px]  nav-droupdowm">
                  <div className="">
                    {/* {isMobile && (
                      <>
                        {isAuthenticated && (
                          <Link
                            to="/dashboard"
                            onClick={() => setIsDropdownOpen(false)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-200"
                          >
                            <span className="font-semibold">Dashboard</span>
                          </Link>
                        )}

                        {isAuthenticated && (
                          <div className="px-4 py-2 text-sm text-gray-700 border-gray-200 flex items-center gap-2">
                            <img src={plan} alt="" className="w-4 h-4" />
                            <span>Pro Plan</span>
                          </div>
                        )}
                      </>
                    )} */}
                    {!isAuthenticated ? (
                      <>
                        {/* <div className="list-item-nav  px-4 hover:bg-gray-100">
                          <img src={userProfile} alt="" />
                          <Link
                            to="/login"
                            onClick={() => setIsDropdownOpen(false)}
                            className="block"
                          >
                            Login
                          </Link>
                        </div> */}

                        <div className="list-item-nav px-4 hover:bg-gray-100 cursor-pointer" onClick={() => {
                          setIsDropdownOpen(false);
                          window.location.href = "/plans";
                        }}>
                          <img src={subscription} alt="" />
                          <span className="block">
                            Subscription & Billing
                          </span>
                        </div>

                        {/* <div className="list-item-nav  px-4 hover:bg-gray-100">
                          <img src={subscription} alt="" />
                          <Link
                            to="/mic-test"
                            onClick={() => setIsDropdownOpen(false)}
                            className="block"
                          >
                            Free Trial
                          </Link>
                        </div> */}
                      </>
                    ) : (
                      <>
                        {/* <div className="list-item-nav px-4 hover:bg-gray-100 cursor-pointer" onClick={() => {
                          setIsDropdownOpen(false);
                          // Add navigation logic here when profile settings page is ready
                        }}>
                          <img src={userProfile} alt="" />
                          <span className="block">
                            Profile Settings
                          </span>
                        </div> */}

                        {/* <div className="list-item-nav px-4 hover:bg-gray-100 cursor-pointer" onClick={(e) => {
                          setIsDropdownOpen(false);
                         
                          if (location.pathname.startsWith('/task')) {
                            stopAllAudio();
                          }
                          window.location.href = "/pricing";
                        }}>
                          <img src={subscription} alt="" />
                          <span className="block">
                            Subscription & Billing
                          </span>
                        </div> */}

                        {/* <div className="list-item-nav  px-4 hover:bg-gray-100">
                          <img src={subscription} alt="" />
                          <Link
                            to="/mic-test"
                            onClick={() => setIsDropdownOpen(false)}
                            className="block"
                          >
                            Free Trial
                          </Link>
                        </div> */}
                        <div className="list-item-nav px-4 hover:bg-gray-100 cursor-pointer" onClick={(e) => {
                          setIsDropdownOpen(false);
                          // Stop all audio when navigating from task pages
                          if (location.pathname.startsWith('/task')) {
                            stopAllAudio();
                          }
                          window.location.href = "/exam-list";
                        }}>
                          <img src={subscription} alt="" />
                          <span className="block">
                            Exam History
                          </span>
                        </div>

                        {/* <div className="list-item-nav px-4 hover:bg-gray-100 cursor-pointer" onClick={() => {
                          setIsDropdownOpen(false);
                        }}>
                          <img src={help} alt="" />
                          <span className="block">
                            Help Center
                          </span>
                        </div> */}

                        <div
                          className="list-item-nav px-4 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setIsDropdownOpen(false);
                            // we will open the modal here in the next step
                            window.dispatchEvent(new Event("open-help-report-modal"));
                          }}
                        >
                          <img src={help} alt="" />
                          <span className="block">
                            Help & Support
                          </span>
                        </div>

                        <div className="list-item-nav  px-4 py-[0.8rem] hover:bg-gray-100 border-t border-gray-200 cursor-pointer" onClick={() => {
                          // Stop all audio when logging out from task pages
                          if (location.pathname.startsWith('/task')) {
                            stopAllAudio();
                          }
                          handleLogout(); // ✅ use context logout
                          setIsDropdownOpen(false);
                          window.location.href = "/login";
                        }}>
                          <img src={logout} alt="" />
                          <button

                            className="block"
                          >
                            Sign Out
                          </button>
                        </div>

                      </>
                    )}
                  </div>

                </div>
              )}
            </div>
          </div>

        </div>
      </div>
      
      {/* Exit Exam Confirmation Modal */}
      <ExitExamDialog
        open={showExitModal}
        onOpenChange={(open) => {
          setShowExitModal(open);
        }}
        onContinue={() => {
          setShowExitModal(false);
        }}
        onConfirm={() => {
          setShowExitModal(false);
          
          // Stop all audio and cleanup
          stopAllAudio();
          
          // Small delay to ensure cleanup completes
          setTimeout(() => {
            if (isAuthenticated) {
              navigate("/dashboard");
            } else {
              window.location.href = "https://prep2pass.ca";
            }
          }, 100);
        }}
      />
    </nav>
  );
};

export default Navbar;

